import { z } from "zod";

import type { KlingDirectVideoPayload } from "../types";
import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { KLING2_6_PRO_RESOLUTION_TO_WIDTH_HEIGHT_MAP } from "../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

const kling3ProParams = z
  .object({
    aspectRatio: z.enum(["16:9", "9:16", "1:1"]).default("9:16"),
    duration: z
      .enum([
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "10",
        "11",
        "12",
        "13",
        "14",
        "15",
      ])
      .default("5"),
    generateAudio: z.boolean().default(true),
    multiPrompt: z.boolean().default(false),
    attachments: z.array(attachment).optional(),
    falVoiceId: z.string().optional(),
    klingVoiceId: z.string().optional(),
    klingElementId: z.string().optional(),
    useCharacterBaseImage: z.boolean().default(true),
    prompt: z.string().max(2500).default(""),
    negativePrompt: z.string().default("blur, distort, and low quality"),
    cfgScale: z.number().min(0).max(1).default(0.5),
  })
  .strip();

export const kling3Pro = defineModel({
  key: "kling_3_pro",
  stepType: "video",
  spec: {
    label: "Kling 3.0 Pro",
    description:
      "Top-tier video generation with cinematic visuals, fluid motion, and custom element support",
    icon: "kling-icon.svg",
    tags: ["video-gen"],
    featured: true,
    info: "Generation takes 5-10 minutes. Optionally connect a Start Image for image-to-video mode (aspect ratio derived from image). Connect Element inputs to include custom references in the generated video.",
    features: {
      aspectRatio: true,
      duration: true,
      attachments: true,
      prompts: true,
      variations: true,
      multiPrompt: true,
    } as const,
    options: {
      attachments: [
        {
          type: "image",
          allowedFormats: ["image/jpeg", "image/png", "image/webp"],
          label: "Start Image",
          slot: "start_image",
          required: false,
        },
        {
          type: "image",
          allowedFormats: ["image/jpeg", "image/png", "image/webp"],
          label: "End Image",
          slot: "end_image",
          required: false,
        },
        // Custom character/object slots — auto-referenced as @Element1, @Element2 in prompt
        // Accepts image or video; toPayload detects the media type
        {
          type: "media",
          allowedFormats: [
            "image/jpeg",
            "image/png",
            "image/webp",
            "video/mp4",
            "video/quicktime",
            "video/webm",
          ],
          label: "Element 1",
          slot: "element_1",
          required: false,
        },
        {
          type: "media",
          allowedFormats: [
            "image/jpeg",
            "image/png",
            "image/webp",
            "video/mp4",
            "video/quicktime",
            "video/webm",
          ],
          label: "Element 2",
          slot: "element_2",
          required: false,
        },
        {
          type: "audio",
          allowedFormats: ["audio/mpeg", "audio/wav"],
          label: "Voice Audio",
          slot: "voice_audio",
          required: false,
        },
      ],
      aspectRatio: {
        items: [
          { label: "16:9", value: "16:9" },
          { label: "9:16", value: "9:16" },
          { label: "1:1", value: "1:1" },
        ],
      },
      duration: {
        items: [
          { label: "3s", value: "3" },
          { label: "4s", value: "4" },
          { label: "5s", value: "5" },
          { label: "6s", value: "6" },
          { label: "7s", value: "7" },
          { label: "8s", value: "8" },
          { label: "9s", value: "9" },
          { label: "10s", value: "10" },
          { label: "11s", value: "11" },
          { label: "12s", value: "12" },
          { label: "13s", value: "13" },
          { label: "14s", value: "14" },
          { label: "15s", value: "15" },
        ],
      },
    },
    params: kling3ProParams,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/fal-ai/kling-video/v3/pro/image-to-video",
    entries: [{ unit: "per_second", unitPriceMicrodollars: 168_000 }],
  },
  providers: [
    {
      provider: "kling",
      modelId: "kling-v3",
      estimateCost(params) {
        const perSecond = params.generateAudio ? 0.168 : 0.112;
        return (
          perSecond * 1_000_000 * Number(params.duration) * (1 + BRAND_MARKUP)
        );
      },
      costPerSecond: 0.168 * 1_000_000 * (1 + BRAND_MARKUP),
      toPayload(params): KlingDirectVideoPayload {
        const startImageUrl = params.attachments?.find(
          (att) => att.slot === "start_image",
        )?.data;
        const endImageUrl = params.attachments?.find(
          (att) => att.slot === "end_image",
        )?.data;

        if (!startImageUrl && !params.prompt) {
          throw new ValidationError(
            "Please provide a prompt for text-to-video generation, or connect a Start Image for image-to-video mode.",
          );
        }
        if (endImageUrl && !startImageUrl) {
          throw new ValidationError(
            "End Image requires a Start Image. Connect a Start Image to use an end frame.",
          );
        }

        if (params.klingElementId) {
          if (!startImageUrl) {
            throw new ValidationError(
              "Kling character generation requires a Start Image when using an advanced element.",
            );
          }

          return {
            model_name: "kling-v3",
            prompt: toKlingAdvancedElementPrompt(params.prompt || "--"),
            aspect_ratio: params.aspectRatio,
            duration: params.duration,
            mode: "pro",
            negative_prompt: params.negativePrompt,
            cfg_scale: params.cfgScale,
            sound: params.generateAudio ? "on" : "off",
            image: startImageUrl,
            ...(endImageUrl ? { image_tail: endImageUrl } : {}),
            element_list: [{ element_id: params.klingElementId }],
          };
        }

        type ImageElement = {
          frontal_image: string;
          reference_images?: string[];
        };
        type VideoElement = { video_url: string };
        const elements: (ImageElement | VideoElement)[] = [];
        const connectedSlotNumbers: number[] = [];

        const slots = ["element_1", "element_2"] as const;
        for (let idx = 0; idx < slots.length; idx++) {
          const att = params.attachments?.find((at) => at.slot === slots[idx]);
          if (!att?.data) continue;
          connectedSlotNumbers.push(idx + 1);
          if (att.mediaKind === "video") {
            elements.push({ video_url: att.data });
          } else {
            elements.push({
              frontal_image: att.data,
              reference_images: [att.data],
            });
          }
        }

        let finalPrompt = params.prompt || "--";
        if (connectedSlotNumbers.length > 0 && finalPrompt !== "--") {
          const missingRefs = connectedSlotNumbers
            .map((num) => `@Element${num}`)
            .filter((ref) => !finalPrompt.includes(ref));
          if (missingRefs.length > 0) {
            finalPrompt = `${finalPrompt} Reference ${missingRefs.join(", ")}`;
          }
        }

        const voiceId =
          params.klingVoiceId ?? (params as { voiceId?: string }).voiceId;
        if (voiceId && finalPrompt && !finalPrompt.includes("<<<voice_1>>>")) {
          finalPrompt = `${finalPrompt} <<<voice_1>>>`;
        }

        // Character-driven generations (elements or voice cloning) use the
        // Omni model, which accepts `elements` + `voice_list` together — the
        // plain `kling-v3` on `/v1/videos/image2video` silently drops
        // `voice_list` and rejects element-only payloads outright.
        const useOmni = voiceId || elements.length > 0;

        return {
          model_name: useOmni ? "kling-v3-omni" : "kling-v3",
          prompt: finalPrompt,
          aspect_ratio: params.aspectRatio,
          duration: params.duration,
          mode: "pro",
          negative_prompt: params.negativePrompt,
          cfg_scale: params.cfgScale,
          sound: params.generateAudio ? "on" : "off",
          ...(startImageUrl ? { image: startImageUrl } : {}),
          ...(endImageUrl ? { image_tail: endImageUrl } : {}),
          ...(elements.length > 0 ? { elements } : {}),
          ...(voiceId ? { voice_list: [{ voice_id: voiceId }] } : {}),
        };
      },
    },
    {
      provider: "fal",
      modelId: "fal-ai/kling-video/v3/pro/image-to-video",
      estimateCost(params) {
        const perSecond = params.generateAudio ? 0.168 : 0.112;
        return (
          perSecond * 1_000_000 * Number(params.duration) * (1 + BRAND_MARKUP)
        );
      },
      costPerSecond: 0.168 * 1_000_000 * (1 + BRAND_MARKUP),
      toPayload(params) {
        const startImageUrl = params.attachments?.find(
          (att) => att.slot === "start_image",
        )?.data;

        // Route to T2V or I2V based on whether a start image is provided.
        // Elements are allowed in both modes; only the start_image switches the
        // endpoint.
        const isI2v = !!startImageUrl;
        const modelUrl = isI2v
          ? "fal-ai/kling-video/v3/pro/image-to-video"
          : "fal-ai/kling-video/v3/pro/text-to-video";

        if (!isI2v && !params.prompt) {
          throw new ValidationError(
            "Please provide a prompt for text-to-video generation, or connect a Start Image for image-to-video mode.",
          );
        }

        // Build elements from attachment slots, tracking slot numbers for
        // @Element refs. Kling expects @Element1 = slot element_1,
        // @Element2 = slot element_2, so the slot number (1-based) is used.
        type ImageElement = {
          frontal_image_url: string;
          reference_image_urls: string[];
          voice_id?: string;
        };
        type VideoElement = { video_url: string; voice_id?: string };
        const elements: (ImageElement | VideoElement)[] = [];
        const connectedSlotNumbers: number[] = [];
        const voiceId =
          params.falVoiceId ?? (params as { voiceId?: string }).voiceId;
        let hasVideoElementVoice = false;

        const slots = ["element_1", "element_2"] as const;
        for (let idx = 0; idx < slots.length; idx++) {
          const att = params.attachments?.find((at) => at.slot === slots[idx]);
          if (!att?.data) continue;

          connectedSlotNumbers.push(idx + 1);
          if (att.mediaKind === "video") {
            const videoElement: VideoElement = { video_url: att.data };
            if (voiceId && !hasVideoElementVoice) {
              videoElement.voice_id = voiceId;
              hasVideoElementVoice = true;
            }
            elements.push(videoElement);
          } else {
            // Kling requires at least one reference_image_url alongside frontal_image_url
            elements.push({
              frontal_image_url: att.data,
              reference_image_urls: [att.data],
            });
          }
        }

        // Auto-append only missing @Element references using slot numbers
        let promptWithRefs = params.prompt || undefined;
        if (connectedSlotNumbers.length > 0 && promptWithRefs) {
          const currentPrompt = promptWithRefs;
          const missingRefs = connectedSlotNumbers
            .map((num) => `@Element${num}`)
            .filter((ref) => !currentPrompt.includes(ref));
          if (missingRefs.length > 0) {
            promptWithRefs = `${currentPrompt} Reference ${missingRefs.join(", ")}`;
          }
        }

        let finalPromptWithVoice = promptWithRefs;
        if (
          voiceId &&
          !hasVideoElementVoice &&
          finalPromptWithVoice &&
          !finalPromptWithVoice.includes("<<<voice_1>>>")
        ) {
          finalPromptWithVoice = `${finalPromptWithVoice} <<<voice_1>>>`;
        }

        const base = {
          model: modelUrl,
          prompt: finalPromptWithVoice,
          duration: params.duration,
          aspect_ratio: params.aspectRatio,
          negative_prompt: params.negativePrompt,
          cfg_scale: params.cfgScale,
          generate_audio: params.generateAudio,
          num_inference_steps: 20,
          enable_safety_checker: false,
          enable_output_safety_checker: false,
          shift: 5,
          video_quality: "high" as const,
          video_write_mode: "balanced" as const,
        };

        const endImageUrl = params.attachments?.find(
          (att) => att.slot === "end_image",
        )?.data;

        return {
          ...base,
          ...(startImageUrl ? { start_image_url: startImageUrl } : {}),
          ...(endImageUrl ? { end_image_url: endImageUrl } : {}),
          ...(elements.length > 0 ? { elements } : {}),
          ...(voiceId && !hasVideoElementVoice ? { voice_ids: [voiceId] } : {}),
        };
      },
    },
    {
      provider: "runware",
      modelId: "klingai:kling-video@3-pro",
      estimateCost(params) {
        const perSecond = params.generateAudio ? 0.168 : 0.112;
        return (
          perSecond * 1_000_000 * Number(params.duration) * (1 + BRAND_MARKUP)
        );
      },
      costPerSecond: 0.168 * 1_000_000 * (1 + BRAND_MARKUP),
      toPayload(params) {
        const startImageUrl = params.attachments?.find(
          (att) => att.slot === "start_image",
        )?.data;

        const endImageUrl = params.attachments?.find(
          (att) => att.slot === "end_image",
        )?.data;

        const isI2v = !!startImageUrl;

        if (!isI2v && !params.prompt) {
          throw new ValidationError(
            "Please provide a prompt for text-to-video generation, or connect a Start Image for image-to-video mode.",
          );
        }

        // Runware uses inputs.frameImages (string array) for frame images
        const inputFrameImages: string[] = [];
        if (startImageUrl) inputFrameImages.push(startImageUrl);
        if (endImageUrl) inputFrameImages.push(endImageUrl);

        const size =
          KLING2_6_PRO_RESOLUTION_TO_WIDTH_HEIGHT_MAP[params.aspectRatio];

        return {
          model: this.modelId,
          positivePrompt: params.prompt || "--",
          duration: Number(params.duration),
          negativePrompt: params.negativePrompt,
          CFGScale: params.cfgScale,
          ...(inputFrameImages.length > 0
            ? { inputs: { frameImages: inputFrameImages } }
            : { width: size.width, height: size.height }),
          providerSettings: {
            klingai: {
              sound: params.generateAudio,
            },
          },
        };
      },
    },
  ],
});

function toKlingAdvancedElementPrompt(prompt: string): string {
  const normalized = prompt.split("@Element1").join("@element_1");
  if (normalized === "--" || normalized.includes("@element_1")) {
    return normalized;
  }
  return `${normalized} Reference @element_1`;
}
